import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  ApiError,
  confirmPourEmpty,
  listPourChecks,
  setPourCount,
  type PourCheck,
  type PourFillLevel,
} from "../../lib/api";
import { sortedShelfLocations } from "../../lib/shelfLocations";

/**
 * Bottle checks — the screen that makes public.pour_checks visible to staff (Phase 3b).
 *
 * Two kinds of row land here, in one list with a kind chip:
 *   - empty_check: the 15-minute sync saw Square's pour count at or below zero on a flagged
 *     bottle. Either the bottle really is finished (Empty) or the count drifted (set the count).
 *   - comp_reminder: pours sold with no Bottle Opened for Service comp rung. Nothing to confirm —
 *     staff ring the comp on the next bottle, and can correct the count while they are here.
 *
 * WHAT CLEARS A ROW
 * Only "Empty" resolves a check from this screen: confirm_empty writes `confirmed_empty` directly.
 * Setting a count does NOT resolve it here — the Library sync clears both kinds as
 * `square_corrected` once Square's raw count goes above zero, which is at most 15 minutes later
 * (sync-inventory/modules/pour-checks.ts). So a count is reported as "clears at the next sync",
 * never as done, and a count of zero is steered to Empty, which would otherwise leave Square at or
 * below zero and the check open.
 *
 * SCROLL AND FILTER SURVIVE AN ACTION
 * Rows are never removed or reordered by an action: a resolved row stays in place, greyed, with
 * what happened under it. The filter and the query are their own state and no action touches them.
 * Only an explicit Refresh re-fetches the list.
 *
 * SINGLE-FLIGHT, KEYED ON THE POUR
 * One request per POUR at a time (`inFlight`), matching what the endpoint claims — a bottle with
 * both an empty_check and a comp_reminder open renders two cards over one pour, and both disable
 * together. Ours is the first of the three layers (screen, then the per-pour claim in inflight.ts,
 * then the compare-and-set), so a 409 `busy` reaching this screen means a second tablet and is
 * worded that way. The sibling card does keep the pour_count_version it was painted with, so the
 * first tap on it after its twin saves earns one 409 `changed` and an automatic refresh.
 */

/** Mirrors MAX_MANUAL_POURS in supabase/functions/_shared/pours.ts, which refuses more. */
const MAX_MANUAL_POURS = 200;

type KindFilter = "all" | "empty_check" | "comp_reminder";

interface RowState {
  busy?: boolean;
  /** Set when this row is finished with — the check is resolved and offers no more actions. */
  resolved?: boolean;
  note?: string;
  error?: string;
  exact?: string;
}

/** An action a staff member has chosen but not yet confirmed. The amount is captured at the
 *  moment of arming, so editing the number afterwards cannot change what Confirm sends. */
type ArmedAction =
  | { kind: "empty" }
  | { kind: "fill"; level: PourFillLevel; label: string }
  | { kind: "exact"; pours: number };

const FILL_BUTTONS: { level: PourFillLevel; label: string }[] = [
  { level: "full", label: "Full" },
  { level: "three_quarters", label: "3/4" },
  { level: "half", label: "1/2" },
  { level: "quarter", label: "1/4" },
];

/** Plain English for what Confirm is about to do. Says Square out loud: that is the part staff
 *  cannot undo by reloading, and the reason this screen asks twice. */
function confirmPrompt(action: ArmedAction, bottle: string): string {
  if (action.kind === "empty") {
    return `Mark ${bottle} empty? Square goes to 0, the shelf pin is cleared, and this check closes.`;
  }
  if (action.kind === "fill") {
    return `Set ${bottle} to ${action.label}? The pours are worked out from the bottle size and sent to Square.`;
  }
  return `Set ${bottle} to ${action.pours} pour${action.pours === 1 ? "" : "s"}? That count is sent to Square.`;
}

function kindLabel(kind: string): string {
  if (kind === "empty_check") return "Empty check";
  if (kind === "comp_reminder") return "Comp reminder";
  return kind;
}

/** "3 days" / "4 hours" / "22 minutes" — how long this check has been waiting. */
function waitedFor(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const minutes = Math.floor(ms / 60000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;
  return `${Math.floor(hours / 24)} days`;
}

function whenLabel(iso: string | null): string {
  if (!iso) return "no recorded sale";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "unknown";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * Staff-readable wording for a failed action. The endpoint's own messages are already written for
 * staff, so they are passed through; these add what the screen knows that the message does not.
 * `refresh` means the list on screen is behind the database and should be re-fetched; `done`
 * means this check is settled and the row should stop offering actions.
 */
function describeError(e: unknown): { message: string; refresh: boolean; done?: boolean } {
  if (e instanceof ApiError) {
    if (e.changed) return { message: `${e.message} (Refreshed below.)`, refresh: true };
    if (e.busy) {
      return {
        message:
          "Another tablet is saving this bottle right now. Nothing was changed — refresh and check the count before trying again.",
        refresh: true,
      };
    }
    if (e.saved) return { message: `${e.message} The app catches up within 15 minutes.`, refresh: true };
    if (e.status === 422) return { message: e.message, refresh: false };
    // Not `refresh: true`: re-fetching drops this card, and the explanation goes with it.
    // The row is marked done instead, and the next Refresh clears it.
    if (e.status === 404) {
      // Three server paths return 404: the check is gone, or its pour went inactive or merged.
      // All three mean the same thing to staff — there is nothing left to do on this row.
      return { message: "Nothing left to do here — someone else cleared this, or the bottle left the catalog.", refresh: false, done: true };
    }
    if (e.status === 401) return { message: "This account cannot set counts. Ask a lead or manager.", refresh: false };
    if (e.status === 408) {
      return {
        message:
          "That took too long to answer. Square may or may not have it — refresh and check the count before trying again.",
        refresh: true,
      };
    }
    return { message: e.message, refresh: false };
  }
  return { message: "Couldn't reach the bar system. Check the tablet's connection and try again.", refresh: false };
}

export default function AdminBottleChecks() {
  const [checks, setChecks] = useState<PourCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<Record<string, RowState>>({});
  /** Pour ids with a save in flight. The ref is the guard (synchronous, before any await); the
   *  state is what disables the buttons, including on a second card for the same bottle. */
  const inFlight = useRef<Set<string>>(new Set());
  const [busyPours, setBusyPours] = useState<string[]>([]);
  /** The one action waiting for a Confirm tap, anywhere on the screen. Arming a second action
   *  replaces the first, so there is never more than one primed write. */
  const [armed, setArmed] = useState<{ checkId: string; action: ArmedAction } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPourChecks();
      setChecks(data);
      // An explicit load is a fresh view: last action's notes, errors and typed counts go with it.
      setRows({});
      setArmed(null);
      setLoadError(null);
    } catch (e) {
      setLoadError(
        e instanceof ApiError && e.status === 401
          ? "This account cannot see bottle checks. Ask a lead or manager."
          : "Couldn't load bottle checks. Check the tablet's connection, then refresh."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Re-fetch without touching the filter, the query, or any row's message. */
  const refreshQuietly = useCallback(async () => {
    try {
      setChecks(await listPourChecks());
    } catch {
      /* The row already carries its own error; a failed background refresh adds nothing. */
    }
  }, []);

  const patchRow = useCallback((id: string, patch: RowState) => {
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  /** Prime an action for a Confirm tap. Clears whatever the last one said, so an old message
   *  cannot sit under a fresh prompt and read as if it belonged to it. */
  const arm = useCallback((checkId: string, action: ArmedAction) => {
    setRows((prev) => ({ ...prev, [checkId]: { ...prev[checkId], error: undefined, note: undefined } }));
    setArmed({ checkId, action });
  }, []);

  /** Update one check in place. Never reorders, so the row stays under the staff member's thumb. */
  const patchCheck = useCallback((id: string, patch: Partial<PourCheck>) => {
    setChecks((prev) => prev.map((c) => (c.check_id === id ? { ...c, ...patch } : c)));
  }, []);

  const runEmpty = useCallback(
    async (check: PourCheck) => {
      const id = check.check_id;
      const pourId = check.pour_item_id;
      if (inFlight.current.has(pourId) || check.pour_count_version === null) return;
      inFlight.current.add(pourId);
      setBusyPours((p) => (p.includes(pourId) ? p : [...p, pourId]));
      setArmed(null);
      patchRow(id, { busy: true, note: undefined, error: undefined });
      try {
        const res = await confirmPourEmpty(id, { pour_count_version: check.pour_count_version });
        const shelfNote =
          res.shelf_cleared === false ? " The shelf pin could not be cleared — clear it on the shelf map." : "";
        patchRow(id, {
          busy: false,
          resolved: true,
          note: res.already_resolved
            ? `Already cleared${res.resolved_by ? ` by ${res.resolved_by}` : ""} — nothing was sent to Square.`
            : `Marked empty. Square is at 0 and the check is cleared.${shelfNote}`,
        });
        patchCheck(id, { pours_remaining: 0, pour_status: "out_of_stock" });
      } catch (e) {
        const { message, refresh, done } = describeError(e);
        patchRow(id, { busy: false, error: message, resolved: done || undefined });
        if (refresh) await refreshQuietly();
      } finally {
        inFlight.current.delete(pourId);
        setBusyPours((p) => p.filter((x) => x !== pourId));
      }
    },
    [patchRow, patchCheck, refreshQuietly]
  );

  const runCount = useCallback(
    async (check: PourCheck, amount: { fill: PourFillLevel } | { pours: number }) => {
      const id = check.check_id;
      const pourId = check.pour_item_id;
      if (inFlight.current.has(pourId) || check.pour_count_version === null) return;
      inFlight.current.add(pourId);
      setBusyPours((p) => (p.includes(pourId) ? p : [...p, pourId]));
      setArmed(null);
      patchRow(id, { busy: true, note: undefined, error: undefined });
      try {
        const res = await setPourCount(check.pour_item_id, amount, {
          pour_count_version: check.pour_count_version,
        });
        patchCheck(id, {
          pours_remaining: res.pours_remaining,
          pour_status: res.status,
          pour_count_version: res.pour_count_version,
        });
        const sizeNote =
          "fill" in amount && res.bottle_size_label ? ` (${res.pours_set} pours, ${res.bottle_size_label})` : "";
        patchRow(id, {
          busy: false,
          exact: "",
          note:
            `Count set to ${res.pours_set}${sizeNote}. Square has it now; this check clears at the next sync, ` +
            `within 15 minutes.${res.notice ? ` ${res.notice}` : ""}`,
        });
      } catch (e) {
        const { message, refresh, done } = describeError(e);
        patchRow(id, { busy: false, error: message, resolved: done || undefined });
        if (refresh) await refreshQuietly();
      } finally {
        inFlight.current.delete(pourId);
        setBusyPours((p) => p.filter((x) => x !== pourId));
      }
    },
    [patchRow, patchCheck, refreshQuietly]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return checks.filter((c) => {
      if (kindFilter !== "all" && c.kind !== kindFilter) return false;
      if (!q) return true;
      return `${c.pour_name || ""} ${c.bottle_name || ""}`.toLowerCase().includes(q);
    });
  }, [checks, kindFilter, query]);

  const counts = useMemo(
    () => ({
      all: checks.length,
      empty_check: checks.filter((c) => c.kind === "empty_check").length,
      comp_reminder: checks.filter((c) => c.kind === "comp_reminder").length,
    }),
    [checks]
  );

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Bottle checks</h1>
          <p style={styles.subtitle}>
            Bottles the inventory sync thinks need a human. Empty is the normal state of this screen.
          </p>
        </div>
        <button type="button" style={styles.refreshBtn} onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </header>

      {loadError && <div style={styles.loadError}>{loadError}</div>}

      {checks.length > 0 && (
        <div style={styles.controls}>
          <div style={styles.filterRow}>
            {(["all", "empty_check", "comp_reminder"] as KindFilter[]).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKindFilter(k)}
                style={{ ...styles.filterBtn, ...(kindFilter === k ? styles.filterBtnActive : null) }}
                aria-pressed={kindFilter === k}
              >
                {k === "all" ? "All" : kindLabel(k)} ({counts[k]})
              </button>
            ))}
          </div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by bottle"
            style={styles.search}
            aria-label="Filter by bottle"
          />
        </div>
      )}

      {!loading && checks.length === 0 && !loadError && (
        <div style={styles.empty}>
          <div style={styles.emptyMark}>✓</div>
          <p style={styles.emptyTitle}>Nothing to check.</p>
          <p style={styles.emptyBody}>
            Every open bottle matches what Square holds. New checks appear here within 15 minutes of the sync
            noticing one.
          </p>
        </div>
      )}

      {!loading && checks.length > 0 && visible.length === 0 && (
        <div style={styles.empty}>
          <p style={styles.emptyBody}>No check matches this filter.</p>
        </div>
      )}

      <div style={styles.list}>
        {visible.map((check) => {
          const row = rows[check.check_id] || {};
          const pourBusy = busyPours.includes(check.pour_item_id);
          const armedHere = armed?.checkId === check.check_id;
          const bottleLabel = check.bottle_name || check.pour_name || "this bottle";
          const isEmptyCheck = check.kind === "empty_check";
          const canAct = check.actionable && check.pour_count_version !== null && !row.resolved;
          const shelf = sortedShelfLocations(check.shelf_location || []);
          return (
            <section
              key={check.check_id}
              style={{ ...styles.card, ...(row.resolved ? styles.cardResolved : null) }}
              aria-busy={pourBusy || undefined}
            >
              <div style={styles.chipRow}>
                <span style={{ ...styles.chip, ...(isEmptyCheck ? styles.chipEmpty : styles.chipComp) }}>
                  {kindLabel(check.kind)}
                </span>
                {check.flagged && <span style={{ ...styles.chip, ...styles.chipFlagged }}>Flagged bottle</span>}
                <span style={styles.waited}>waiting {waitedFor(check.opened_at)}</span>
              </div>
              <h2 style={styles.pourName}>{check.pour_name || "This pour is no longer in the catalog"}</h2>
              {check.bottle_name && check.bottle_name !== check.pour_name && (
                <p style={styles.bottleName}>{check.bottle_name}</p>
              )}

              <dl style={styles.facts}>
                <div style={styles.fact}>
                  <dt style={styles.factLabel}>App count</dt>
                  <dd style={styles.factValue}>
                    {check.pours_remaining === null ? "—" : `${check.pours_remaining} pours`}
                    {check.pour_status ? ` · ${check.pour_status.replace(/_/g, " ")}` : ""}
                  </dd>
                </div>
                <div style={styles.fact}>
                  <dt style={styles.factLabel}>Square when opened</dt>
                  <dd style={styles.factValue}>
                    {check.square_pour_count_at_open === null ? "unknown" : check.square_pour_count_at_open}
                  </dd>
                </div>
                <div style={styles.fact}>
                  <dt style={styles.factLabel}>Sealed in reserve</dt>
                  <dd style={styles.factValue}>
                    {check.sealed_bottle_count === null ? "unknown" : check.sealed_bottle_count}
                  </dd>
                </div>
                <div style={styles.fact}>
                  <dt style={styles.factLabel}>Last sale</dt>
                  <dd style={styles.factValue}>{whenLabel(check.last_sale_at)}</dd>
                </div>
                <div style={{ ...styles.fact, ...styles.factWide }}>
                  <dt style={styles.factLabel}>Shelf</dt>
                  <dd style={styles.factValue}>
                    {shelf.length === 0
                      ? "not on the shelf map"
                      : shelf.map((s) => `${s.code} — ${s.area}${s.shelf ? `, ${s.shelf}` : ""}`).join(" · ")}
                  </dd>
                </div>
              </dl>

              {!isEmptyCheck && (
                <p style={styles.compHint}>
                  Ring <strong>Bottle Opened for Service</strong> on the next bottle. If this one's count is
                  wrong, set it here.
                </p>
              )}

              {!check.actionable && (
                <p style={styles.gone}>
                  This pour is no longer active in the catalog, so there is nothing to set. It will clear itself
                  from this list.
                </p>
              )}

              {canAct && armedHere && (
                /* Every button below writes to Square, which a reload cannot undo. One tap arms,
                   one confirms, and the prompt names the bottle and the number being sent. */
                <div style={styles.confirmBar} role="status">
                  <p style={styles.confirmText}>{confirmPrompt(armed.action, bottleLabel)}</p>
                  <div style={styles.confirmButtons}>
                    <button
                      type="button"
                      style={{ ...styles.btn, ...styles.btnConfirm, ...(pourBusy ? styles.btnBusy : null) }}
                      onClick={() => {
                        const a = armed.action;
                        if (a.kind === "empty") runEmpty(check);
                        else if (a.kind === "fill") runCount(check, { fill: a.level });
                        else runCount(check, { pours: a.pours });
                      }}
                      disabled={pourBusy}
                    >
                      Confirm
                    </button>
                    <button type="button" style={styles.btnCancel} onClick={() => setArmed(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {canAct && !armedHere && (
                <div style={styles.actions}>
                  {isEmptyCheck && (
                    <button
                      type="button"
                      style={{ ...styles.btn, ...styles.btnEmpty, ...(pourBusy ? styles.btnBusy : null) }}
                      onClick={() => arm(check.check_id, { kind: "empty" })}
                      disabled={pourBusy}
                    >
                      Empty
                    </button>
                  )}
                  {FILL_BUTTONS.map((f) => (
                    <button
                      key={f.level}
                      type="button"
                      style={{ ...styles.btn, ...(pourBusy ? styles.btnBusy : null) }}
                      onClick={() => arm(check.check_id, { kind: "fill", level: f.level, label: f.label })}
                      disabled={pourBusy}
                    >
                      {f.label}
                    </button>
                  ))}
                  <form
                    style={styles.exactForm}
                    onSubmit={(e) => {
                      e.preventDefault();
                      const n = Number(row.exact);
                      // Digits only: Number.isInteger accepts "1e3" as 1000, which nobody typed
                      // meaning a thousand pours.
                      if (!/^\d+$/.test((row.exact || "").trim()) || !Number.isInteger(n)) {
                        patchRow(check.check_id, { error: "Enter a whole number of pours." });
                        return;
                      }
                      if (n > MAX_MANUAL_POURS) {
                        patchRow(check.check_id, {
                          error: `That is more than ${MAX_MANUAL_POURS} pours — check the number.`,
                        });
                        return;
                      }
                      if (n === 0) {
                        patchRow(check.check_id, {
                          error: isEmptyCheck
                            ? "Use Empty for a finished bottle — a count of 0 leaves the check open."
                            : "A count of 0 means the bottle is finished; clear it from that bottle's empty check.",
                        });
                        return;
                      }
                      arm(check.check_id, { kind: "exact", pours: n });
                    }}
                  >
                    <input
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      value={row.exact ?? ""}
                      onChange={(e) => patchRow(check.check_id, { exact: e.target.value })}
                      placeholder="pours"
                      style={styles.exactInput}
                      aria-label={`Exact pours left for ${check.pour_name || "this bottle"}`}
                      disabled={pourBusy}
                    />
                    <button
                      type="submit"
                      style={{ ...styles.btn, ...(pourBusy ? styles.btnBusy : null) }}
                      disabled={pourBusy}
                    >
                      Set
                    </button>
                  </form>
                </div>
              )}

              {/* A save writes to Square; its outcome is announced, not only shown. */}
              {row.busy && <p style={styles.busyLine}>Sending to Square…</p>}
              {pourBusy && !row.busy && !row.error && !row.note && (
                <p style={styles.busyLine}>Another check on this bottle is being saved — wait for it to finish.</p>
              )}
              {row.note && (
                <p style={styles.note} role="status">
                  {row.note}
                </p>
              )}
              {row.error && (
                <p style={styles.error} role="alert">
                  {row.error}
                </p>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { padding: "24px 20px 60px", maxWidth: 900, margin: "0 auto", color: "var(--text, #F7F5F0)" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 26,
    fontWeight: 400,
    margin: "0 0 4px",
    color: "var(--text, #F7F5F0)",
  },
  subtitle: { color: "var(--muted, #A39D98)", fontSize: 14, margin: 0, maxWidth: 520 },
  refreshBtn: {
    background: "transparent",
    color: "var(--gold, #F9AF36)",
    border: "1px solid var(--gold, #F9AF36)",
    padding: "8px 18px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 44,
  },
  loadError: {
    border: "1px solid #C0392B",
    background: "rgba(192,57,43,0.12)",
    color: "var(--text, #F7F5F0)",
    padding: "10px 14px",
    borderRadius: 6,
    fontSize: 14,
    marginBottom: 16,
  },
  controls: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  filterRow: { display: "flex", gap: 8, flexWrap: "wrap" },
  filterBtn: {
    minHeight: 40,
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    color: "var(--muted, #A39D98)",
    padding: "8px 14px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  filterBtnActive: { color: "var(--gold, #F9AF36)", borderColor: "var(--gold, #F9AF36)" },
  search: {
    background: "var(--dark-1, #0D0B08)",
    border: "1px solid var(--border, #231D19)",
    color: "var(--text, #F7F5F0)",
    padding: "8px 12px",
    borderRadius: 6,
    fontSize: 14,
    minWidth: 180,
    minHeight: 40,
    flex: "1 1 180px",
    maxWidth: 280,
  },
  empty: { textAlign: "center", padding: "56px 20px", color: "var(--muted, #A39D98)" },
  emptyMark: { fontSize: 34, color: "var(--gold, #F9AF36)", marginBottom: 8 },
  emptyTitle: { fontFamily: "Georgia, serif", fontSize: 20, color: "var(--text, #F7F5F0)", margin: "0 0 6px" },
  emptyBody: { fontSize: 14, margin: "0 auto", maxWidth: 420, lineHeight: 1.5 },
  list: { display: "flex", flexDirection: "column", gap: 14 },
  card: {
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    padding: "16px 16px 14px",
    background: "var(--dark-1, #0D0B08)",
  },
  cardResolved: { opacity: 0.6 },
  chipRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 },
  chip: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    padding: "3px 8px",
    borderRadius: 4,
  },
  chipEmpty: { background: "rgba(230,126,34,0.16)", color: "#E67E22" },
  chipComp: { background: "rgba(74,143,217,0.16)", color: "#4A8FD9" },
  chipFlagged: { background: "rgba(249,175,54,0.16)", color: "var(--gold, #F9AF36)" },
  waited: { fontSize: 12, color: "var(--muted, #A39D98)" },
  pourName: {
    fontFamily: "Georgia, serif",
    fontSize: 19,
    fontWeight: 400,
    margin: 0,
    color: "var(--text, #F7F5F0)",
  },
  bottleName: { fontSize: 13, color: "var(--muted, #A39D98)", margin: "2px 0 0" },
  facts: { display: "flex", flexWrap: "wrap", gap: "10px 22px", margin: "12px 0 0" },
  fact: { margin: 0, minWidth: 110 },
  factWide: { flexBasis: "100%" },
  factLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "var(--muted, #A39D98)",
    margin: 0,
  },
  factValue: { fontSize: 14, color: "var(--text, #F7F5F0)", margin: "2px 0 0" },
  compHint: {
    fontSize: 13,
    color: "var(--text, #F7F5F0)",
    background: "rgba(74,143,217,0.10)",
    border: "1px solid rgba(74,143,217,0.35)",
    borderRadius: 6,
    padding: "8px 12px",
    margin: "12px 0 0",
    lineHeight: 1.5,
  },
  gone: { fontSize: 13, color: "var(--muted, #A39D98)", margin: "12px 0 0", lineHeight: 1.5 },
  actions: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 14 },
  btn: {
    background: "transparent",
    border: "1px solid var(--border, #231D19)",
    color: "var(--text, #F7F5F0)",
    padding: "10px 16px",
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 44,
    minWidth: 56,
  },
  btnEmpty: { background: "var(--gold, #F9AF36)", color: "#0D0B08", borderColor: "var(--gold, #F9AF36)" },
  confirmBar: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    background: "rgba(249,175,54,0.08)",
    border: "1px solid rgba(249,175,54,0.45)",
    borderRadius: 10,
    padding: 12,
    marginTop: 4,
  },
  confirmText: { fontSize: 13, lineHeight: 1.5, margin: 0, flex: "1 1 260px", color: "var(--text, #F7F5F0)" },
  confirmButtons: { display: "flex", gap: 8 },
  btnConfirm: { background: "var(--gold, #F9AF36)", color: "#0D0B08", borderColor: "var(--gold, #F9AF36)", fontWeight: 800 },
  btnCancel: {
    background: "transparent",
    border: "1px solid var(--border, #2A2723)",
    color: "var(--muted, #A39D98)",
    borderRadius: 8,
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    minHeight: 44,
    minWidth: 72,
  },
  btnBusy: { opacity: 0.5, cursor: "progress" },
  exactForm: { display: "flex", gap: 8, alignItems: "center" },
  exactInput: {
    background: "var(--dark-1, #0D0B08)",
    border: "1px solid var(--border, #231D19)",
    color: "var(--text, #F7F5F0)",
    padding: "10px 12px",
    borderRadius: 6,
    fontSize: 14,
    width: 88,
    minHeight: 44,
  },
  busyLine: { fontSize: 13, color: "var(--muted, #A39D98)", margin: "10px 0 0" },
  note: { fontSize: 13, color: "var(--text, #F7F5F0)", margin: "10px 0 0", lineHeight: 1.5 },
  error: { fontSize: 13, color: "#E8897F", margin: "10px 0 0", lineHeight: 1.5 },
};
